import { mdiClose, mdiLink } from '@mdi/js';
import Icon from '@mdi/react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LinkModalProps {
	open: boolean;
	onClose: () => void;
	onConfirm: (url: string, text: string) => void;
	initialUrl?: string;
	initialText?: string;
	mode: 'create' | 'edit';
}

export const LinkModal: React.FC<LinkModalProps> = ({
	open,
	onClose,
	onConfirm,
	initialUrl = '',
	initialText = '',
	mode,
}) => {
	const { t } = useTranslation();
	const [url, setUrl] = useState(initialUrl);
	const [text, setText] = useState(initialText);
	const [urlError, setUrlError] = useState('');

	// Reset form when modal opens/closes
	useEffect(() => {
		if (open) {
			setUrl(initialUrl);
			setText(initialText);
			setUrlError('');
		}
	}, [open, initialUrl, initialText]);

	// Validate URL
	const validateUrl = (urlString: string): boolean => {
		if (!urlString.trim()) {
			setUrlError(t('chats.urlRequired'));
			return false;
		}

		try {
			// Add protocol if missing for validation
			const testUrl = urlString.startsWith('http') ? urlString : `https://${urlString}`;
			new URL(testUrl);
			setUrlError('');
			return true;
		} catch {
			setUrlError(t('chats.validUrlRequired'));
			return false;
		}
	};

	const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newUrl = e.target.value;
		setUrl(newUrl);

		// Clear error when user starts typing
		if (urlError && newUrl.trim()) {
			setUrlError('');
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (validateUrl(url)) {
			// Ensure URL has protocol
			const finalUrl = url.startsWith('http') ? url : `https://${url}`;
			const finalText = text.trim() || finalUrl;

			onConfirm(finalUrl, finalText);
			onClose();
		}
	};

	const handleCancel = () => {
		onClose();
	};

	// Auto-fill text field if URL is entered and text is empty
	useEffect(() => {
		if (url && !text && mode === 'create') {
			// Extract domain or use full URL as text
			try {
				const testUrl = url.startsWith('http') ? url : `https://${url}`;
				const urlObj = new URL(testUrl);
				setText(urlObj.hostname.replace(/^www\./, '') || url);
			} catch {
				setText(url);
			}
		}
	}, [url, text, mode]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: '12px',
					boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
				},
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					pb: 1,
					borderBottom: '1px solid',
					borderColor: 'divider',
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<Icon path={mdiLink} size={0.9} color="#1976d2" />
					<Typography variant="h6" component="div">
						{mode === 'create' ? t('chats.addLink') : t('chats.editLink')}
					</Typography>
				</Box>
				<IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
					<Icon path={mdiClose} size={0.8} />
				</IconButton>
			</DialogTitle>

			<form onSubmit={handleSubmit}>
				<DialogContent sx={{ pt: 3, pb: 2 }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
						<Box>
							<Typography
								variant="subtitle2"
								sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}
							>
								{t('chats.url')}
							</Typography>
							<TextField
								fullWidth
								value={url}
								onChange={handleUrlChange}
								placeholder={t('chats.urlPlaceholder')}
								error={!!urlError}
								helperText={urlError}
								autoFocus
								variant="outlined"
								size="medium"
								sx={{
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
							/>
						</Box>

						<Box>
							<Typography
								variant="subtitle2"
								sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}
							>
								{t('chats.displayText')}
							</Typography>
							<TextField
								fullWidth
								value={text}
								onChange={(e) => setText(e.target.value)}
								placeholder={t('chats.linkTextOptional')}
								variant="outlined"
								size="medium"
								sx={{
									'& .MuiOutlinedInput-root': {
										borderRadius: '8px',
									},
								}}
							/>
							<Typography
								variant="caption"
								sx={{ mt: 0.5, color: 'text.secondary', display: 'block' }}
							>
								{t('chats.linkTextHint')}
							</Typography>
						</Box>
					</Box>
				</DialogContent>

				<DialogActions
					sx={{
						px: 3,
						pb: 3,
						pt: 1,
						gap: 1,
						borderTop: '1px solid',
						borderColor: 'divider',
					}}
				>
					<Button
						onClick={handleCancel}
						variant="outlined"
						sx={{
							borderRadius: '8px',
							textTransform: 'none',
							fontWeight: 500,
							minWidth: '80px',
						}}
					>
						{t('chats.cancel')}
					</Button>
					<Button
						type="submit"
						variant="contained"
						sx={{
							borderRadius: '8px',
							textTransform: 'none',
							fontWeight: 500,
							minWidth: '80px',
							boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
						}}
					>
						{mode === 'create' ? t('chats.addLink') : t('chats.updateLink')}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
